import { Check, Copy, Monitor, Moon, Rows3, Rows4, Sun } from "lucide-react";
import * as React from "react";

import {
  useEnableTrustedBrowser,
  useForgetTrustedBrowser,
  useSession,
  useTrustedBrowser,
} from "@/api/auth";
import { type TenantPlugin, useTenantInfo } from "@/api/tenants";
import { EmptyState } from "@/components/layout/EmptyState";
import { PageHeader } from "@/components/layout/PageHeader";
import { EntitlementPanel } from "@/routes/settings/EntitlementPanel";
import { SecuritySection } from "@/routes/settings/SecuritySection";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { applyDensityAttr, type Density, useDensityStore } from "@/stores/density";
import { applyThemeClass, type Theme, useThemeStore } from "@/stores/theme";

/**
 * Settings page — session + tenant info, SDK setup, and the
 * display-preference toggles the rest of the app reads from
 * persisted Zustand stores (theme + density).
 *
 * Rewritten on top of the P1.1a design system: PageHeader, surface-2
 * tokens, state-cached palette for the "available" plugin badge, and
 * the same dl/dt/dd grid the run-detail page uses for hash rows.
 */
export function SettingsPage(): JSX.Element {
  const session = useSession();
  const tenant = useTenantInfo();

  if (session.isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-9 w-1/3" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (!session.data) {
    return (
      <EmptyState
        title="Sign in to view settings."
        description="This page requires an active session."
      />
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Settings"
        description="Pilot tenant alpha. P1.0 will land OIDC + per-user RBAC."
      />

      <Card as="section" aria-labelledby="display-heading" className="flex flex-col gap-3 p-card">
        <SectionHeading id="display-heading" title="Display" />
        <DefinitionGrid>
          <DefinitionRow label="Theme">
            <ThemePicker />
          </DefinitionRow>
          <DefinitionRow label="Density">
            <DensityPicker />
          </DefinitionRow>
        </DefinitionGrid>
      </Card>

      <Card as="section" aria-labelledby="you-heading" className="flex flex-col gap-3 p-card">
        <SectionHeading id="you-heading" title="You" />
        <DefinitionGrid>
          <DefinitionRow label="Display name">{session.data.display_name}</DefinitionRow>
          <DefinitionRow label="Email" mono>
            {session.data.email}
          </DefinitionRow>
          <DefinitionRow label="Session issued">
            {formatTimestamp(session.data.issued_at)}
          </DefinitionRow>
          <DefinitionRow label="Session expires">
            {formatTimestamp(session.data.expires_at)}
          </DefinitionRow>
        </DefinitionGrid>
      </Card>

      <SecuritySection />

      <TrustedBrowserSection />

      <Card as="section" aria-labelledby="tenant-heading" className="flex flex-col gap-3 p-card">
        <SectionHeading id="tenant-heading" title="Tenant" />
        {tenant.isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : tenant.isError || !tenant.data ? (
          <p className="text-sm text-muted-foreground">
            Falling back to session-derived info: tenant{" "}
            <span className="font-mono">{session.data.tenant_slug}</span> (
            {session.data.tenant_display_name}).
          </p>
        ) : (
          <>
            <DefinitionGrid>
              <DefinitionRow label="Display name">{tenant.data.display_name}</DefinitionRow>
              <DefinitionRow label="Slug" mono>
                {tenant.data.slug}
              </DefinitionRow>
              <DefinitionRow label="Security level">L{tenant.data.security_level}</DefinitionRow>
            </DefinitionGrid>
            <Separator />
            <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Calculator plugins
            </h3>
            <PluginsTable plugins={tenant.data.plugins} />
          </>
        )}
      </Card>

      <Card as="section" aria-labelledby="sdk-heading" className="flex flex-col gap-3 p-card">
        <SectionHeading id="sdk-heading" title="SDK access" />
        <p className="text-sm text-muted-foreground">
          Install the FlowStack SDK and authenticate against this tenant.
        </p>
        <CodeBlock
          code={`pip install flowstack
flowstack auth pilot --tenant ${session.data.tenant_slug} --email ${session.data.email}`}
        />
      </Card>

      <Card as="section" aria-labelledby="apps-heading" className="flex flex-col gap-3 p-card">
        <SectionHeading id="apps-heading" title="ATTMOS apps" />
        <EntitlementPanel />
      </Card>

      <Card as="section" aria-labelledby="shortcuts-heading" className="flex flex-col gap-3 p-card">
        <SectionHeading id="shortcuts-heading" title="Keyboard shortcuts" />
        <p className="text-sm text-muted-foreground">
          The command palette + Linear-style two-key navigation are always available.
        </p>
        <ShortcutsTable />
      </Card>
    </div>
  );
}

// ---- Trusted browser -------------------------------------------------

/**
 * Opt-in "remember me": trusting the browser mints a 30-day
 * ``flowstack_trust`` cookie the backend uses to silently re-mint the
 * 8h session (``POST /api/auth/refresh``) — so this browser survives
 * session expiry and server restarts without a trip to the Hosted UI.
 *
 * Hidden only when the backend CONFIRMS ``available: false`` (no
 * database configured / feature not deployed). While the status is
 * loading a skeleton holds the slot, and a failed status fetch shows
 * a retry affordance — a security-adjacent control must not silently
 * vanish on a transient error (Rupert PR-596 r0).
 */
function TrustedBrowserSection(): JSX.Element | null {
  const status = useTrustedBrowser();
  const enable = useEnableTrustedBrowser();
  const forget = useForgetTrustedBrowser();

  if (status.isLoading) {
    return <Skeleton className="h-24 w-full" />;
  }
  if (status.isError) {
    return (
      <Card
        as="section"
        aria-labelledby="trusted-browser-heading"
        className="flex flex-col gap-3 p-card"
      >
        <SectionHeading id="trusted-browser-heading" title="Trust this browser" />
        <p className="text-sm text-muted-foreground" role="alert">
          Couldn&apos;t load the trusted-browser status.
        </p>
        <div>
          <Button type="button" variant="outline" size="sm" onClick={() => void status.refetch()}>
            Retry
          </Button>
        </div>
      </Card>
    );
  }
  if (!status.data?.available) {
    return null;
  }
  const trusted = status.data.trusted;
  const busy = enable.isPending || forget.isPending;

  return (
    <Card
      as="section"
      aria-labelledby="trusted-browser-heading"
      className="flex flex-col gap-3 p-card"
    >
      <SectionHeading id="trusted-browser-heading" title="Trust this browser" />
      <p className="text-sm text-muted-foreground">
        A trusted browser stays signed in for 30 days — the session renews silently instead of
        sending you back to the sign-in page. Only enable this on a device you control. Signing out
        revokes the trust.
      </p>
      <DefinitionGrid>
        <DefinitionRow label="Status">
          {trusted && status.data.expires_at
            ? `Trusted until ${formatTimestamp(status.data.expires_at)}`
            : trusted
              ? "Trusted"
              : "Not trusted"}
        </DefinitionRow>
        <DefinitionRow label="Action">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={() => {
              // Reset the *other* mutation so a stale error banner
              // from a previous action doesn't outlive a later
              // successful one — React Query mutations never
              // auto-reset (Rupert PR-596 r0).
              if (trusted) {
                enable.reset();
                forget.mutate();
              } else {
                forget.reset();
                enable.mutate();
              }
            }}
          >
            {trusted ? "Forget this browser" : "Trust this browser"}
          </Button>
        </DefinitionRow>
      </DefinitionGrid>
      {(enable.isError || forget.isError) && (
        <p className="text-xs text-destructive" role="alert">
          Couldn&apos;t update the trusted-browser setting. Try again.
        </p>
      )}
    </Card>
  );
}

// ---- Pickers ---------------------------------------------------------

const THEMES: Array<{
  value: Theme;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}> = [
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
  { value: "system", label: "System", icon: Monitor },
];

function ThemePicker(): JSX.Element {
  const theme = useThemeStore((s) => s.theme);
  const setTheme = useThemeStore((s) => s.setTheme);
  return (
    <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Theme">
      {THEMES.map(({ value, label, icon: Icon }) => (
        <PickerOption
          key={value}
          selected={theme === value}
          onClick={() => {
            setTheme(value);
            applyThemeClass(value);
          }}
          icon={<Icon className="h-3.5 w-3.5" aria-hidden />}
          label={label}
          name="theme"
        />
      ))}
    </div>
  );
}

const DENSITIES: Array<{
  value: Density;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}> = [
  { value: "comfortable", label: "Comfortable", icon: Rows3 },
  { value: "compact", label: "Compact", icon: Rows4 },
];

function DensityPicker(): JSX.Element {
  const density = useDensityStore((s) => s.density);
  const setDensity = useDensityStore((s) => s.setDensity);
  return (
    <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Density">
      {DENSITIES.map(({ value, label, icon: Icon }) => (
        <PickerOption
          key={value}
          selected={density === value}
          onClick={() => {
            setDensity(value);
            applyDensityAttr(value);
          }}
          icon={<Icon className="h-3.5 w-3.5" aria-hidden />}
          label={label}
          name="density"
        />
      ))}
    </div>
  );
}

interface PickerOptionProps {
  selected: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  name: string;
}

function PickerOption({ selected, onClick, icon, label, name }: PickerOptionProps): JSX.Element {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-label={`${name}: ${label}`}
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs transition-colors",
        selected
          ? "border-cyan/60 bg-surface-2 text-foreground shadow-[inset_2px_0_0_hsl(var(--ring))]"
          : "border-border-subtle bg-background text-muted-foreground hover:border-border-strong hover:bg-surface-2 hover:text-foreground"
      )}
    >
      {icon}
      {label}
    </button>
  );
}

// ---- Definition grid -------------------------------------------------

function SectionHeading({ id, title }: { id: string; title: string }): JSX.Element {
  return (
    <h2 id={id} className="font-display text-base font-semibold tracking-tight">
      {title}
    </h2>
  );
}

function DefinitionGrid({ children }: { children: React.ReactNode }): JSX.Element {
  return <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-[180px_1fr]">{children}</dl>;
}

function DefinitionRow({
  label,
  children,
  mono,
}: {
  label: string;
  children: React.ReactNode;
  mono?: boolean;
}): JSX.Element {
  return (
    <>
      <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </dt>
      <dd className={cn("text-sm text-foreground", mono && "font-mono text-[12px]")}>{children}</dd>
    </>
  );
}

// ---- Plugins ---------------------------------------------------------

function PluginsTable({ plugins }: { plugins: TenantPlugin[] }): JSX.Element {
  if (plugins.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        No calculator plugins declared on this tenant&apos;s image variant.
      </p>
    );
  }
  return (
    <div className="overflow-hidden rounded-md border border-border-subtle">
      <Table>
        <TableHeader surface="tinted">
          <TableRow>
            <TableHead>Plugin</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>License</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {plugins.map((p) => (
            <TableRow key={p.plugin_id} interactive="hover">
              <TableCell>
                <span className="font-mono text-[12px] text-foreground">{p.plugin_id}</span>
                <span className="ml-2 text-xs text-muted-foreground">{p.display_name}</span>
              </TableCell>
              <TableCell>
                {p.available ? (
                  <span className="rounded-full border border-state-cached-border bg-state-cached-bg px-2 py-0.5 text-[11px] uppercase tracking-wider text-state-cached-fg">
                    available
                  </span>
                ) : (
                  <span className="rounded-full border border-border-subtle bg-surface-2 px-2 py-0.5 text-[11px] uppercase tracking-wider text-muted-foreground">
                    not licensed
                  </span>
                )}
              </TableCell>
              <TableCell className="text-xs text-muted-foreground">
                {p.license_note ?? "—"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// ---- Code block + copy-to-clipboard ---------------------------------

function CodeBlock({ code }: { code: string }): JSX.Element {
  const [copied, setCopied] = React.useState(false);
  const copy = (): void => {
    // Per Rupert PR-270 r0: ``navigator.clipboard`` is undefined in
    // insecure HTTP contexts, embedded webviews, and older Safari —
    // accessing ``.writeText`` on undefined throws *synchronously*,
    // which the .then() rejection handler can't catch. Guard the
    // access so "fail silently" stays silent instead of crashing.
    if (!navigator.clipboard?.writeText) return;
    navigator.clipboard.writeText(code).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1600);
      },
      () => {
        // Permission denied — fail silently rather than toasting
        // noise for a feature people rarely use.
      }
    );
  };
  return (
    <div className="relative">
      <pre className="overflow-x-auto rounded-md border border-border-subtle bg-surface-2 p-3 pr-12 font-mono text-[12px] leading-relaxed">
        <code>{code}</code>
      </pre>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={copy}
        aria-label={copied ? "Copied" : "Copy to clipboard"}
        className="absolute right-1.5 top-1.5 h-7 w-7"
      >
        {copied ? (
          <Check className="h-3.5 w-3.5 text-state-cached-fg" aria-hidden />
        ) : (
          <Copy className="h-3.5 w-3.5" aria-hidden />
        )}
      </Button>
    </div>
  );
}

// ---- Shortcuts -------------------------------------------------------

const SHORTCUTS: Array<{ keys: string[]; description: string }> = [
  { keys: ["⌘", "K"], description: "Open the command palette" },
  { keys: ["g", "p"], description: "Go to Projects" },
  { keys: ["g", "r"], description: "Go to Runs" },
  { keys: ["g", "a"], description: "Go to Artifacts" },
  { keys: ["g", "b"], description: "Go to Budget" },
  { keys: ["g", "s"], description: "Go to Settings" },
  { keys: ["/"], description: "Focus search (opens command palette)" },
  { keys: ["?"], description: "Open the command palette (alternate)" },
];

function ShortcutsTable(): JSX.Element {
  return (
    <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      {SHORTCUTS.map((s) => (
        <li
          key={s.keys.join("+")}
          className="flex items-center justify-between gap-3 rounded-md border border-border-subtle bg-surface-2/50 px-3 py-2"
        >
          <span className="text-xs text-muted-foreground">{s.description}</span>
          <span className="flex items-center gap-1">
            {s.keys.map((key, i) => (
              <React.Fragment key={i}>
                {i > 0 && <span className="text-[10px] text-muted-foreground">then</span>}
                <kbd className="rounded border border-border-subtle bg-background px-1.5 py-0.5 font-mono text-[11px] text-foreground">
                  {key}
                </kbd>
              </React.Fragment>
            ))}
          </span>
        </li>
      ))}
    </ul>
  );
}

// ---- Misc ------------------------------------------------------------

function formatTimestamp(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
